import { isDevMode, NgModule, Injector } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { APP_BASE_HREF, DatePipe } from '@angular/common';
import { provideAnimations } from '@angular/platform-browser/animations';
import { LayoutModule } from '@angular/cdk/layout';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { OktaAuthModule, OktaConfig } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';

import environment from '@environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AuthModule } from './auth/auth.module';
import { AppComponent } from './app.component';
import { AuthInterceptor } from './auth/auth.interceptor';
import { ErrorInterceptor } from './data-access/shared/intercerptors/error.interceptor';
import { MaintenanceInterceptor } from './data-access/shared/intercerptors/maintenance.interceptor';
import { LoadingComponent } from './loading/loading.component';

import { LastUrlInterceptor } from './data-access/shared/intercerptors/last-url-interceptor.interceptor';
import { MaterialItemsModule } from './shared/material-items.module';

import { UserModule } from './user/user.module';
import { SharedModule } from './shared/shared.module';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import {
  provideStoreDevtools,
  StoreDevtoolsModule,
} from '@ngrx/store-devtools';

import { HolidayClosureComponent } from './shared/components/holiday-closure/holiday-closure.component';
import { ERMWorkspaceModule } from './setup/workspace/erm-workspace.module';
import {
  BuildLinkFromValue,
  ExtractValueFromPathPipe,
  WcgBannerComponent,
  WcgCustomPipesModule,
  WcgDateOnlyPipe,
  WcgDateTimePipe,
  WcgDialogModule,
  WcgTimeOnlyPipe,
  WcgTruncateDirective,
} from '@wcg/frontend-framework';
import { MatDialogModule } from '@angular/material/dialog';
import { MainHeaderComponent } from './main-header/main-header.component';
import { HeaderuserMenuComponent } from '@user/headeruser-menu/headeruser-menu.component';

import { WebSocketEffects } from './websocket/redux/websocket.effects';
import { TimeoutModule } from './timeout/timeout.module';
import { provideStoreModules } from './providers';
import {
  GRID_VIEW_FEATURE_STATE,
  GridViewEffects,
} from '@data-access/grid-views';
import { HttpRequestDataInterceptor } from './data-access/shared/intercerptors/request-data-interceptor';
import { TalkdeskComponent } from '@shared/components/talkdesk/talkdesk.component';
import { AssignmentEffects } from '@data-access/assignment/effects';
import {
  DownloadExportsEffects,
  DOWNLOADS_FEATURE_STATE,
  EXPORTS_FEATURE_STATE,
} from '@data-access/downloads-exports';
import { FullNamePipe } from '@shared/pipes/fullname.pipe';
import { SESSION_PROVIDER } from './auth/custom-providers/logout-provider';
import { RolesEffect } from '@data-access/roles';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Router } from '@angular/router';
import { ERMDatePipe, ERMDateTimePipe } from '@shared/pipes/erm-date.pipe';

const config: any = {
  clientId: environment.Okta.clientId,
  issuer: environment.Okta.issuer,
  redirectUri: environment.Okta.redirectUri,
  scopes: ['openid', 'profile', 'email', 'Clinsphere_Scope'],
  tokenManager: {
    autoRenew: true,
    storage: 'localStorage',
  },
};
const oktaAuth = new OktaAuth(config);

export function onAuthRequired(_: unknown, injector: Injector): void {
  const router = injector.get(Router);
  router.navigate(['/auth/login']);
}

const moduleConfig: OktaConfig = { oktaAuth, onAuthRequired };

@NgModule({
  bootstrap: [AppComponent],
  declarations: [AppComponent, LoadingComponent, HolidayClosureComponent],
  exports: [],
  imports: [
    AppRoutingModule,
    AuthModule,
    BrowserModule,
    HeaderuserMenuComponent,
    ERMWorkspaceModule,
    LayoutModule,
    MainHeaderComponent,
    MatDialogModule,
    MaterialItemsModule,
    OktaAuthModule.forRoot(moduleConfig),
    SharedModule,
    StoreModule.forRoot(provideStoreModules()),
    StoreModule.forFeature(GRID_VIEW_FEATURE_STATE),
    EffectsModule.forRoot([
      AssignmentEffects,
      GridViewEffects,
      WebSocketEffects,
      DownloadExportsEffects,
      RolesEffect,
    ]),
    StoreModule.forFeature(EXPORTS_FEATURE_STATE),
    StoreModule.forFeature(DOWNLOADS_FEATURE_STATE),
    StoreDevtoolsModule.instrument({
      connectInZone: true,
      logOnly: environment.production,
      maxAge: 25,
    }),
    TimeoutModule,
    UserModule,
    WcgBannerComponent,
    WcgDialogModule,
    WcgCustomPipesModule,
    WcgTruncateDirective,
    TalkdeskComponent,
    NgxSkeletonLoaderModule,
  ],

  providers: [
    ERMDatePipe,
    ERMDateTimePipe,
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: LastUrlInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MaintenanceInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MaintenanceInterceptor,
      multi: true,
    },
    {
      multi: true,
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRequestDataInterceptor,
    },
    { provide: APP_BASE_HREF, useValue: environment.appBaseHref },
    WcgDateTimePipe,
    WcgDateOnlyPipe,
    WcgTimeOnlyPipe,
    DatePipe,
    FullNamePipe,
    SESSION_PROVIDER,
    provideStoreDevtools({
      logOnly: !isDevMode(),
      maxAge: 25,
    }),

    BuildLinkFromValue,
    ExtractValueFromPathPipe,
  ],
})
export class AppModule {}
